import { useEffect, useRef, useState } from "react";
import { api, formatApiError } from "../../lib/api";
import { Input } from "../ui/input";
import { Button } from "../ui/button";
import { Plus, Sparkles, Mic, Square, Camera, Loader2, Pencil } from "lucide-react";
import { toast } from "sonner";
import { localDayISO } from "./utils";

/**
 * The Nutrition Log's AI Meal Parser block — text / voice / photo input,
 * live portion-scaling, and a "Log this meal" button that writes a nutrition
 * entry tagged with the parse source ("text" | "voice" | "photo").
 *
 * Owns all of its own state so `NutritionLogCard` can stay focused on the
 * manual-entry form + totals. Parent supplies:
 *   - mealGroup  → optional breakfast/lunch/dinner/snack tag for the entry
 *   - aiEnabled  → hides voice + photo when the feature flag is off
 *   - onLogged() → called after a successful POST /nutrition so the parent
 *                  can reload today's totals + fire onEntryChange.
 */
export default function AIMealParser({ mealGroup, aiEnabled, onLogged }) {
  const [aiText, setAiText] = useState("");
  const [aiParsing, setAiParsing] = useState(false);
  const [aiResult, setAiResult] = useState(null);
  const [aiMode, setAiMode] = useState("text"); // "text" | "voice" | "photo"

  // Live per-item portion multipliers (baseline items are frozen when we get a result)
  const [scales, setScales] = useState({});   // { idx: multiplier }
  const [editingIdx, setEditingIdx] = useState(null);
  const [editDraft, setEditDraft] = useState("");

  // ---- Voice recording (MediaRecorder) ----
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const audioStreamRef = useRef(null);
  const [recording, setRecording] = useState(false);
  const [recordingSec, setRecordingSec] = useState(0);
  const recordingTimerRef = useRef(null);

  // ---- Photo upload ----
  const photoInputRef = useRef(null);

  // Reset portion scales whenever a new AI result arrives
  useEffect(() => {
    if (aiResult && Array.isArray(aiResult.items)) {
      const base = {};
      aiResult.items.forEach((_, i) => { base[i] = 1; });
      setScales(base);
      setEditingIdx(null);
    } else {
      setScales({});
    }
  }, [aiResult]);

  // Live scaled items + totals derived from `scales`
  const scaledItems = (aiResult?.items || []).map((it, i) => {
    const m = scales[i] ?? 1;
    return {
      ...it,
      _mult: m,
      calories: Math.round(Number(it.calories || 0) * m),
      protein_g: Math.round(Number(it.protein_g || 0) * m * 10) / 10,
      carbs_g: Math.round(Number(it.carbs_g || 0) * m * 10) / 10,
      fat_g: Math.round(Number(it.fat_g || 0) * m * 10) / 10,
    };
  });
  const scaledTotals = scaledItems.reduce((acc, it) => ({
    calories: acc.calories + it.calories,
    protein_g: Math.round((acc.protein_g + it.protein_g) * 10) / 10,
    carbs_g: Math.round((acc.carbs_g + it.carbs_g) * 10) / 10,
    fat_g: Math.round((acc.fat_g + it.fat_g) * 10) / 10,
  }), { calories: 0, protein_g: 0, carbs_g: 0, fat_g: 0 });

  const parseWithAI = async () => {
    const txt = aiText.trim();
    if (!txt) { toast.error("Describe what you ate"); return; }
    setAiParsing(true);
    setAiMode("text");
    try {
      const { data } = await api.post("/nutrition/parse", { text: txt });
      if (!data.items || data.items.length === 0) {
        toast.error("Couldn't identify any foods — try being more specific");
      }
      setAiResult(data);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setAiParsing(false); }
  };

  // ---- Voice: record + transcribe ----
  const startRecording = async () => {
    if (recording) return;
    if (!navigator.mediaDevices || typeof MediaRecorder === "undefined") {
      toast.error("Voice recording isn't supported in this browser");
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      audioStreamRef.current = stream;
      // Pick a MIME the browser can produce; audio/webm is universally supported
      const mime = MediaRecorder.isTypeSupported("audio/webm;codecs=opus")
        ? "audio/webm;codecs=opus"
        : (MediaRecorder.isTypeSupported("audio/webm") ? "audio/webm" : "");
      const rec = mime ? new MediaRecorder(stream, { mimeType: mime }) : new MediaRecorder(stream);
      mediaRecorderRef.current = rec;
      audioChunksRef.current = [];
      rec.ondataavailable = (e) => { if (e.data && e.data.size > 0) audioChunksRef.current.push(e.data); };
      rec.onstop = () => sendRecordedAudio();
      rec.start();
      setRecording(true);
      setRecordingSec(0);
      recordingTimerRef.current = setInterval(() => {
        setRecordingSec((s) => {
          const next = s + 1;
          if (next >= 60) { stopRecording(); }  // hard cap 60s
          return next;
        });
      }, 1000);
    } catch (err) {
      toast.error("Microphone access denied");
    }
  };

  const stopRecording = () => {
    if (recordingTimerRef.current) { clearInterval(recordingTimerRef.current); recordingTimerRef.current = null; }
    const rec = mediaRecorderRef.current;
    if (rec && rec.state !== "inactive") rec.stop();
    if (audioStreamRef.current) {
      audioStreamRef.current.getTracks().forEach((t) => t.stop());
      audioStreamRef.current = null;
    }
    setRecording(false);
  };

  const sendRecordedAudio = async () => {
    const chunks = audioChunksRef.current;
    if (!chunks || chunks.length === 0) return;
    const blob = new Blob(chunks, { type: mediaRecorderRef.current?.mimeType || "audio/webm" });
    audioChunksRef.current = [];
    if (blob.size < 800) {
      toast.error("Recording too short — try again");
      return;
    }
    setAiMode("voice");
    setAiParsing(true);
    try {
      const form = new FormData();
      form.append("audio", blob, "meal.webm");
      const { data } = await api.post("/nutrition/parse-audio", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (data.transcript) setAiText(data.transcript);
      if (!data.items || data.items.length === 0) {
        toast.error("Heard you, but couldn't identify any foods");
      }
      setAiResult(data);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setAiParsing(false); }
  };

  // ---- Photo: upload + vision parse ----
  const onPhotoChosen = async (e) => {
    const file = e.target.files?.[0];
    if (photoInputRef.current) photoInputRef.current.value = "";  // allow re-pick same file
    if (!file) return;
    if (!/^image\/(jpeg|png|webp)$/i.test(file.type)) {
      toast.error("Use a JPEG, PNG, or WebP photo");
      return;
    }
    if (file.size > 8 * 1024 * 1024) {
      toast.error("Photo too large (8MB max)");
      return;
    }
    setAiMode("photo");
    setAiParsing(true);
    try {
      const form = new FormData();
      form.append("image", file, file.name || "meal.jpg");
      const { data } = await api.post("/nutrition/parse-image", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (!data.items || data.items.length === 0) {
        toast.error(data.warning || "Couldn't identify foods in this photo");
      }
      setAiResult(data);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setAiParsing(false); }
  };

  const setItemScale = (idx, mult) => {
    const clamped = Math.max(0, Math.min(20, Number(mult) || 0));
    setScales((prev) => ({ ...prev, [idx]: clamped }));
  };

  const commitEditDraft = () => {
    if (editingIdx == null) return;
    const parsed = parseFloat(editDraft);
    if (Number.isFinite(parsed) && parsed >= 0) {
      setItemScale(editingIdx, parsed);
    }
    setEditingIdx(null);
    setEditDraft("");
  };

  const logAIResult = async () => {
    if (!aiResult) return;
    try {
      await api.post("/nutrition", {
        calories: scaledTotals.calories,
        protein_g: scaledTotals.protein_g,
        carbs_g: scaledTotals.carbs_g,
        fat_g: scaledTotals.fat_g,
        meal_group: mealGroup || null,
        source: aiMode, // "text" | "voice" | "photo" — powers the Recent AI Parses ribbon
        note: scaledItems.map((i) => i.name).slice(0, 3).join(", "),
        thumbnail_b64: aiMode === "photo" ? (aiResult?.thumbnail_b64 || null) : null,
        day: localDayISO(),
      });
      setAiText("");
      setAiResult(null);
      onLogged?.();
      toast.success("Meal logged");
    } catch (err) { toast.error(formatApiError(err)); }
  };

  // Feature flag: when the admin has flipped `feature_flags.ai_nutrition` off,
  // render nothing at all — no notice, no parser. Users see only the manual form
  // and meal presets. This runs AFTER all hooks so Rules of Hooks stay happy.
  if (!aiEnabled) {
    return null;
  }

  return (
    <div className="mb-4 medtech-panel p-4" data-testid="ai-meal-parser">
      <div className="flex items-center gap-2 mb-2">
        <Sparkles className="w-3.5 h-3.5 medtech-accent" />
        <div className="font-mono-num text-[11px] uppercase tracking-[0.3em] medtech-accent">
          // AI Meal Parser
        </div>
        <span className="ml-auto font-mono-num text-[9px] uppercase tracking-widest text-slate-500">
          {aiMode === "voice" ? "Whisper + Claude" : aiMode === "photo" ? "Claude Vision" : "Powered by Claude"}
        </span>
      </div>
      <div className="flex flex-wrap gap-2">
        <Input
          value={aiText}
          onChange={(e) => setAiText(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter" && !aiParsing) parseWithAI(); }}
          placeholder={recording ? "Listening…" : "e.g. one large egg, two slices of toast, coffee with milk"}
          maxLength={500}
          disabled={aiParsing || recording}
          className="flex-1 min-w-[240px] bg-slate-50 border-slate-200 rounded-none h-10"
          data-testid="ai-parser-input"
        />

        {/* Voice mic button */}
        {aiEnabled && (
        <Button
          type="button"
          onClick={recording ? stopRecording : startRecording}
          disabled={aiParsing}
          title={recording ? "Stop recording" : "Dictate your meal"}
          className={[
            "rounded-none h-10 px-3 uppercase tracking-widest text-[10px] font-mono-num font-semibold",
            recording ? "bg-[#FF3B30] hover:bg-[#e6362b] text-white animate-pulse" : "bg-slate-50 hover:bg-slate-200 text-slate-800 dark:text-white border border-[var(--amino-teal-border)]",
          ].join(" ")}
          data-testid={recording ? "ai-parser-stop-voice" : "ai-parser-start-voice"}
        >
          {recording
            ? <><Square className="w-3.5 h-3.5 mr-1.5" />{recordingSec}s · Stop</>
            : <><Mic className="w-3.5 h-3.5 mr-1.5" />Voice</>
          }
        </Button>
        )}

        {/* Photo upload button */}
        {aiEnabled && (<>
        <input
          ref={photoInputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          capture="environment"
          onChange={onPhotoChosen}
          className="hidden"
          data-testid="ai-parser-photo-input"
        />
        <Button
          type="button"
          onClick={() => photoInputRef.current?.click()}
          disabled={aiParsing || recording}
          title="Snap or upload a meal photo"
          className="rounded-none h-10 px-3 uppercase tracking-widest text-[10px] font-mono-num font-semibold bg-slate-50 hover:bg-slate-200 text-slate-800 dark:text-white border border-[var(--amino-teal-border)]"
          data-testid="ai-parser-photo-btn"
        >
          <Camera className="w-3.5 h-3.5 mr-1.5" />Photo
        </Button>
        </>)}

        <Button
          type="button"
          onClick={parseWithAI}
          disabled={aiParsing || recording}
          className="rounded-none h-10 px-4 uppercase tracking-widest text-[10px] font-mono-num font-semibold"
          style={{ background: "var(--amino-teal)", color: "#052e2b" }}
          data-testid="ai-parser-analyze"
        >
          {aiParsing
            ? <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />{aiMode === "voice" ? "Transcribing…" : aiMode === "photo" ? "Looking…" : "Analyzing…"}</>
            : <><Sparkles className="w-3.5 h-3.5 mr-1.5" />Analyze</>
          }
        </Button>
      </div>
      {aiResult && scaledItems.length > 0 && (
        <div className="mt-3 space-y-1.5" data-testid="ai-parser-result">
          <div className="font-mono-num text-[9px] uppercase tracking-widest text-slate-400 flex items-center gap-2">
            <Pencil className="w-3 h-3" /> Tap a portion to adjust — macros scale live
          </div>
          {scaledItems.map((it, i) => {
            const isEditing = editingIdx === i;
            const mult = scales[i] ?? 1;
            return (
              <div key={i} className="flex items-center justify-between border border-slate-200 bg-slate-50 px-3 py-1.5 gap-2" data-testid={`ai-parser-item-${i}`}>
                <div className="min-w-0 flex-1 flex items-baseline gap-2">
                  <span className="text-sm text-slate-900 dark:text-white truncate">{it.name}</span>
                  {isEditing ? (
                    <div className="inline-flex items-center gap-1">
                      <span className="font-mono-num text-[10px] text-slate-500 uppercase">×</span>
                      <Input
                        type="number"
                        step="0.1"
                        min="0"
                        max="20"
                        autoFocus
                        value={editDraft}
                        onChange={(e) => setEditDraft(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") commitEditDraft();
                          if (e.key === "Escape") { setEditingIdx(null); setEditDraft(""); }
                        }}
                        onBlur={commitEditDraft}
                        className="w-16 h-6 bg-slate-50 border-slate-300 rounded-none font-mono-num text-xs px-1.5"
                        data-testid={`ai-parser-item-${i}-scale-input`}
                      />
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={() => { setEditingIdx(i); setEditDraft(String(mult)); }}
                      className="font-mono-num text-[10px] text-slate-500 uppercase hover:text-[var(--amino-teal)] transition-colors border border-transparent hover:border-[var(--amino-teal-border)] px-1.5 py-0.5 -my-0.5 inline-flex items-center gap-1"
                      title="Adjust portion"
                      data-testid={`ai-parser-item-${i}-scale`}
                    >
                      {it.quantity && <span>{it.quantity}</span>}
                      {mult !== 1 && <span className="text-[var(--amino-teal)]">× {mult}</span>}
                      <Pencil className="w-2.5 h-2.5 opacity-70" />
                    </button>
                  )}
                </div>
                <div className="font-mono-num text-[11px] text-slate-600 tabular-nums whitespace-nowrap">
                  {it.calories} kcal · {it.protein_g}g P · {it.carbs_g}g C · {it.fat_g}g F
                </div>
              </div>
            );
          })}
          <div className="flex items-center justify-between border border-[var(--amino-teal-border)] bg-[var(--amino-teal-soft)] px-3 py-2 mt-1">
            <div className="font-mono-num text-[10px] uppercase tracking-widest medtech-accent">Totals</div>
            <div className="font-mono-num text-sm text-slate-900 dark:text-white tabular-nums">
              <b>{scaledTotals.calories}</b> kcal · <b>{scaledTotals.protein_g}</b>g P · <b>{scaledTotals.carbs_g}</b>g C · <b>{scaledTotals.fat_g}</b>g F
            </div>
          </div>
          <div className="flex gap-2 mt-1">
            <Button
              type="button"
              onClick={logAIResult}
              className="flex-1 bg-[#0D9488] hover:bg-[#0F766E] text-black rounded-none h-9 uppercase tracking-widest text-[10px] font-mono-num font-semibold"
              data-testid="ai-parser-log"
            >
              <Plus className="w-3.5 h-3.5 mr-1.5" /> Log this meal
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => setAiResult(null)}
              className="border-slate-200 bg-transparent hover:bg-slate-50 rounded-none h-9 px-3 uppercase tracking-widest text-[10px] font-mono-num"
              data-testid="ai-parser-clear"
            >
              Clear
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
